/**
 * Sáng tác nhạc nền bằng code (B3) — generative, gieo hạt được.
 *
 * Thuần 100%: không WebAudio, không DOM — chỉ sinh dữ liệu nốt nhạc.
 * Engine (engine.ts) chịu trách nhiệm render ra WebAudio.
 * Cùng (id, hạt) luôn cho cùng một bản nhạc (test được bằng vitest/node).
 *
 * 4 bản theo master-plan: Phố chợ / Bãi biển / Chợ đêm / đêm.
 * Mỗi bản là một vòng lặp liền mạch (kết ở chủ âm, nốt không tràn qua
 * biên vòng lặp) nên loop không bị "cụp" tiếng.
 */
import { taoRng } from '../core/rng.js';
import type { SuKienNhieu } from './hieu-ung.js';

/** 4 track nhạc theo src/data/am-thanh.json. */
export type IdTrackNhac = 'nhac-pho-cho' | 'nhac-bai-bien' | 'nhac-cho-dem' | 'nhac-dem';

export type KieuSongNhac = 'sine' | 'triangle' | 'square' | 'sawtooth';

/** Một nốt nhạc trong vòng lặp (thời gian tính bằng giây từ đầu vòng). */
export interface NotNhac {
  batDau: number;
  thoiLuong: number;
  tanSo: number;
  kieuSong: KieuSongNhac;
  /** Gain đỉnh 0..1. */
  doDay: number;
  loai: 'giai-dieu' | 'hoa-am' | 'bass';
}

/** Một bản nhạc đã sáng tác xong: vòng lặp + texture môi trường. */
export interface BanNhac {
  id: IdTrackNhac;
  tempo: number;
  /** Số nhịp mỗi ô (luôn 4/4). */
  nhip: number;
  soO: number;
  /** Độ dài một vòng lặp (giây). */
  tongThoiGian: number;
  not: NotNhac[];
  /** Texture nhiễu (sóng biển, dế đêm...) lồng trong vòng lặp. */
  nhieu: SuKienNhieu[];
}

/** Hạt mặc định cho bản "chính thức" của mỗi track (ổn định giữa các phiên). */
export const HAT_MAC_DINH: Record<IdTrackNhac, number> = {
  'nhac-pho-cho': 20261017,
  'nhac-bai-bien': 20261018,
  'nhac-cho-dem': 20261019,
  'nhac-dem': 20261020,
};

/** Tần số (Hz) từ nốt MIDI. */
function tanSoMidi(midi: number): number {
  return 440 * Math.pow(2, (midi - 69) / 12);
}

/** Kẹp MIDI vào quãng loa laptop phát được (A1..C8). */
function kepMidi(midi: number): number {
  return Math.max(33, Math.min(96, Math.round(midi)));
}

interface ThongSoTrack {
  gocMidi: number;
  /** Thang ngũ cung (bán cung tính từ nốt gốc). */
  thangAm: number[];
  tempo: number;
  soO: number;
  /** Mỗi ô: các nốt hợp âm (bán cung từ nốt gốc). */
  hopAm: number[][];
  /** Mỗi ô: nốt gốc hợp âm (bán cung từ nốt gốc) cho bè bass. */
  gocHopAm: number[];
  /** Số slot giai điệu mỗi ô. 0 = không có giai điệu (chỉ pad). */
  buocGiaiDieu: number;
  xacSuatNghi: number;
  swing: boolean;
  kieuGiaiDieu: KieuSongNhac;
  doDayGiaiDieu: number;
  doDayHoaAm: number;
  doDayBass: number;
  /** 'pad' = hợp âm kéo dài cả ô; 'stab' = điểm hợp âm ngắn. */
  kieuHoaAm: 'pad' | 'stab';
  nhieuNen: 'song-bien' | 'de-dem' | 'khong';
}

const THONG_SO: Record<IdTrackNhac, ThongSoTrack> = {
  'nhac-pho-cho': {
    // Chợ quê vui: ngũ cung trưởng trên Đô, tempo nhanh, bass đi đều.
    gocMidi: 60,
    thangAm: [0, 2, 4, 7, 9],
    tempo: 116,
    soO: 8,
    hopAm: [
      [0, 4, 7],
      [9, 12, 16],
      [5, 9, 12],
      [7, 11, 14],
      [0, 4, 7],
      [9, 12, 16],
      [5, 9, 12],
      [7, 11, 14],
    ],
    gocHopAm: [0, 9, 5, 7, 0, 9, 5, 7],
    buocGiaiDieu: 8,
    xacSuatNghi: 0.22,
    swing: false,
    kieuGiaiDieu: 'triangle',
    doDayGiaiDieu: 0.14,
    doDayHoaAm: 0.05,
    doDayBass: 0.1,
    kieuHoaAm: 'pad',
    nhieuNen: 'khong',
  },
  'nhac-bai-bien': {
    // Biển lặng: pad maj7 thưa, sóng vỗ 3 đợt mỗi vòng.
    gocMidi: 57,
    thangAm: [0, 2, 4, 7, 9],
    tempo: 72,
    soO: 8,
    hopAm: [
      [0, 4, 7, 11],
      [0, 4, 7, 11],
      [7, 11, 14, 17],
      [7, 11, 14, 17],
      [9, 12, 16, 19],
      [9, 12, 16, 19],
      [5, 9, 12, 16],
      [5, 9, 12, 16],
    ],
    gocHopAm: [0, 0, 7, 7, 9, 9, 5, 5],
    buocGiaiDieu: 4,
    xacSuatNghi: 0.45,
    swing: false,
    kieuGiaiDieu: 'sine',
    doDayGiaiDieu: 0.12,
    doDayHoaAm: 0.045,
    doDayBass: 0.09,
    kieuHoaAm: 'pad',
    nhieuNen: 'song-bien',
  },
  'nhac-cho-dem': {
    // Chợ đêm: ngũ cung thứ trên Mi, swing nhẹ, bass walking.
    gocMidi: 52,
    thangAm: [0, 3, 5, 7, 10],
    tempo: 96,
    soO: 8,
    hopAm: [
      [0, 3, 7],
      [0, 3, 7],
      [-2, 2, 5],
      [-2, 2, 5],
      [-4, 0, 3],
      [-4, 0, 3],
      [-2, 2, 5],
      [-2, 2, 5],
    ],
    gocHopAm: [0, 0, -2, -2, -4, -4, -2, -2],
    buocGiaiDieu: 8,
    xacSuatNghi: 0.3,
    swing: true,
    kieuGiaiDieu: 'triangle',
    doDayGiaiDieu: 0.13,
    doDayHoaAm: 0.05,
    doDayBass: 0.11,
    kieuHoaAm: 'stab',
    nhieuNen: 'khong',
  },
  'nhac-dem': {
    // Đêm khuya: chỉ pad chậm + tiếng dế + gió nhẹ. Không giai điệu.
    gocMidi: 45,
    thangAm: [0, 3, 5, 7, 10],
    tempo: 48,
    soO: 8,
    hopAm: [
      [0, 3, 7],
      [0, 3, 7],
      [-4, 0, 3],
      [-4, 0, 3],
      [3, 7, 10],
      [3, 7, 10],
      [-2, 2, 5],
      [-2, 2, 5],
    ],
    gocHopAm: [0, 0, -4, -4, -9, -9, -2, -2],
    buocGiaiDieu: 0,
    xacSuatNghi: 0,
    swing: false,
    kieuGiaiDieu: 'sine',
    doDayGiaiDieu: 0,
    doDayHoaAm: 0.05,
    doDayBass: 0.07,
    kieuHoaAm: 'pad',
    nhieuNen: 'de-dem',
  },
};

/**
 * Sáng tác một bản nhạc. Thuần + deterministic: cùng (id, hat) cho cùng
 * kết quả (so sánh deep-equal được trong test).
 */
export function soanNhac(id: IdTrackNhac, hat: number = HAT_MAC_DINH[id]): BanNhac {
  const ts = THONG_SO[id];
  const rng = taoRng(hat);
  const nhip = 4;
  const doDaiBeat = 60 / ts.tempo;
  const doDaiO = doDaiBeat * nhip;
  const tongThoiGian = doDaiO * ts.soO;
  const not: NotNhac[] = [];
  const nhieu: SuKienNhieu[] = [];

  const day = (n: NotNhac): void => {
    // Không để nốt tràn qua biên vòng lặp (loop liền mạch).
    const thoiLuong = Math.min(n.thoiLuong, Math.max(0.05, tongThoiGian - n.batDau));
    not.push({ ...n, thoiLuong });
  };

  // --- Bè bass: nốt gốc hợp âm ---
  for (let o = 0; o < ts.soO; o++) {
    const goc = ts.gocHopAm[o % ts.gocHopAm.length]!;
    if (ts.buocGiaiDieu === 0) {
      // Bản đêm: bass whole-note trầm.
      day({
        batDau: o * doDaiO,
        thoiLuong: doDaiO * 0.95,
        tanSo: tanSoMidi(kepMidi(ts.gocMidi - 12 + goc)),
        kieuSong: 'sine',
        doDay: ts.doDayBass,
        loai: 'bass',
      });
    } else if (ts.swing) {
      // Walking bass: 4 nốt đen (gốc, gốc, năm, gốc).
      const mau = [goc, goc, goc + 7, goc];
      for (let b = 0; b < nhip; b++) {
        day({
          batDau: o * doDaiO + b * doDaiBeat,
          thoiLuong: doDaiBeat * 0.9,
          tanSo: tanSoMidi(kepMidi(ts.gocMidi - 12 + mau[b]!)),
          kieuSong: 'sine',
          doDay: ts.doDayBass,
          loai: 'bass',
        });
      }
    } else {
      // Bass đều: gốc mỗi nhịp, nhảy quãng tám ở ô chẵn.
      for (let b = 0; b < nhip; b++) {
        const nhay = o % 2 === 0 && b === 2 ? 12 : 0;
        day({
          batDau: o * doDaiO + b * doDaiBeat,
          thoiLuong: doDaiBeat * 0.9,
          tanSo: tanSoMidi(kepMidi(ts.gocMidi - 12 + goc + nhay)),
          kieuSong: 'sine',
          doDay: ts.doDayBass,
          loai: 'bass',
        });
      }
    }
  }

  // --- Bè hòa âm ---
  for (let o = 0; o < ts.soO; o++) {
    const hopAm = ts.hopAm[o % ts.hopAm.length]!;
    if (ts.kieuHoaAm === 'pad') {
      for (const nuaCung of hopAm) {
        day({
          batDau: o * doDaiO,
          thoiLuong: doDaiO * 0.98,
          tanSo: tanSoMidi(kepMidi(ts.gocMidi + nuaCung)),
          kieuSong: 'sine',
          doDay: ts.doDayHoaAm,
          loai: 'hoa-am',
        });
      }
    } else {
      // Stab: điểm hợp âm ở nhịp 1 và 3.
      for (const nhipStab of [0, 2]) {
        for (const nuaCung of hopAm) {
          day({
            batDau: o * doDaiO + nhipStab * doDaiBeat,
            thoiLuong: doDaiBeat * 0.55,
            tanSo: tanSoMidi(kepMidi(ts.gocMidi + nuaCung)),
            kieuSong: 'triangle',
            doDay: ts.doDayHoaAm,
            loai: 'hoa-am',
          });
        }
      }
    }
  }

  // --- Giai điệu: random walk trên thang ngũ cung (thứ tự rng cố định) ---
  if (ts.buocGiaiDieu > 0) {
    const doDaiSlot = doDaiO / ts.buocGiaiDieu;
    const soBac = ts.thangAm.length;
    let bac = soBac; // bắt đầu ở quãng tám trên nốt gốc
    for (let o = 0; o < ts.soO; o++) {
      for (let s = 0; s < ts.buocGiaiDieu; s++) {
        let batDau = o * doDaiO + s * doDaiSlot;
        if (ts.swing && s % 2 === 1) batDau += doDaiSlot * 0.33; // trễ off-beat
        const rNghi = rng();
        const rBuoc = rng();
        if (rNghi < ts.xacSuatNghi) continue;
        bac = Math.max(0, Math.min(soBac * 2 - 1, bac + Math.floor(rBuoc * 5) - 2));
        const midi = ts.gocMidi + 12 + Math.floor(bac / soBac) * 12 + ts.thangAm[bac % soBac]!;
        day({
          batDau,
          thoiLuong: doDaiSlot * 1.6,
          tanSo: tanSoMidi(kepMidi(midi)),
          kieuSong: ts.kieuGiaiDieu,
          doDay: ts.doDayGiaiDieu,
          loai: 'giai-dieu',
        });
      }
    }
    // Kết vòng ở chủ âm (loop liền mạch, không "cụp").
    not.push({
      batDau: tongThoiGian - doDaiBeat * 2,
      thoiLuong: doDaiBeat * 1.9,
      tanSo: tanSoMidi(kepMidi(ts.gocMidi + 12)),
      kieuSong: ts.kieuGiaiDieu,
      doDay: ts.doDayGiaiDieu,
      loai: 'giai-dieu',
    });
  }

  // --- Texture môi trường ---
  if (ts.nhieuNen === 'song-bien') {
    for (let i = 0; i < 3; i++) {
      const batDau = (i * tongThoiGian) / 3 + (rng() * 4 - 2);
      nhieu.push({
        loai: 'nhieu',
        batDau: Math.max(0, batDau),
        thoiLuong: 7,
        tanSoLoc: 350,
        tanSoLocCuoi: 900,
        doDay: 0.05,
        dangBao: 'song',
      });
    }
  } else if (ts.nhieuNen === 'de-dem') {
    // Tiếng dế: chirp sine cao, ngắn, rải ngẫu nhiên (như texture giai điệu).
    for (let i = 0; i < 18; i++) {
      const batDau = rng() * (tongThoiGian - 1);
      const tanSo = 4200 + rng() * 600;
      const thoiLuong = 0.05 + rng() * 0.04;
      not.push({
        batDau,
        thoiLuong,
        tanSo,
        kieuSong: 'sine',
        doDay: 0.018,
        loai: 'giai-dieu',
      });
    }
    // Gió đêm nhẹ: 2 đợt dài.
    for (let i = 0; i < 2; i++) {
      nhieu.push({
        loai: 'nhieu',
        batDau: (i * tongThoiGian) / 2 + rng() * 3,
        thoiLuong: 14,
        tanSoLoc: 200,
        tanSoLocCuoi: 320,
        doDay: 0.03,
        dangBao: 'song',
      });
    }
  }

  not.sort((a, b) => a.batDau - b.batDau);
  return { id, tempo: ts.tempo, nhip, soO: ts.soO, tongThoiGian, not, nhieu };
}

/** Danh sách đầy đủ 4 track. */
export const CAC_TRACK_NHAC: readonly IdTrackNhac[] = [
  'nhac-pho-cho',
  'nhac-bai-bien',
  'nhac-cho-dem',
  'nhac-dem',
];

/** true nếu id là track nhạc hợp lệ. */
export function laTrackNhac(id: string): id is IdTrackNhac {
  return (CAC_TRACK_NHAC as readonly string[]).includes(id);
}

/**
 * Chọn track nhạc theo khu vực và giờ game (thuần, test được).
 * Đêm (21h–5h) ưu tiên track đêm bất kể khu.
 */
export function chonTrack(khuId: string, gio: number): IdTrackNhac {
  if (gio >= 21 || gio < 5) return 'nhac-dem';
  switch (khuId) {
    case 'bai-bien':
      return 'nhac-bai-bien';
    case 'cho-dem':
      return 'nhac-cho-dem';
    default:
      return 'nhac-pho-cho';
  }
}
